import React, { useState, useRef, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { PREDEFINED_SKILLS } from '@/constants';

export function SkillsSelector({ selectedSkills = [], onSkillsChange, placeholder = "Type to search skills..." }) {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleSelect = (skill) => {
    if (!selectedSkills.includes(skill)) {
      onSkillsChange([...selectedSkills, skill]);
      setInputValue('');
      setShowDropdown(false);
      setHighlightedIndex(0);
      // Focus back on input
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleRemove = (skillToRemove) => {
    onSkillsChange(selectedSkills.filter(skill => skill !== skillToRemove));
  };

  const filteredSkills = PREDEFINED_SKILLS.filter(skill => 
    skill.toLowerCase().includes(inputValue.toLowerCase()) &&
    !selectedSkills.includes(skill)
  ).slice(0, 10);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        Math.min(prev + 1, filteredSkills.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && filteredSkills.length > 0) {
        handleSelect(filteredSkills[highlightedIndex]);
      } else if (inputValue.trim()) {
        // Allow custom skills if not in predefined list
        const trimmedValue = inputValue.trim();
        if (!selectedSkills.includes(trimmedValue)) {
          onSkillsChange([...selectedSkills, trimmedValue]);
          setInputValue('');
        }
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIndex(0);
    } else if (e.key === 'Backspace' && !inputValue && selectedSkills.length > 0) {
      // Remove last skill on backspace if input is empty
      onSkillsChange(selectedSkills.slice(0, -1));
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(value.length > 0);
    setHighlightedIndex(0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-2 relative" ref={dropdownRef}>
      <div className="min-h-[42px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-slate-400 focus-within:ring-offset-2">
        <div className="flex flex-wrap gap-2">
          {selectedSkills.map((skill) => (
            <Badge key={skill} variant="secondary" className="gap-1 pr-1">
              {skill}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleRemove(skill);
                }}
                className="ml-1 rounded-full hover:bg-slate-300"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length > 0 && setShowDropdown(true)}
            placeholder={selectedSkills.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] outline-none bg-transparent"
          />
        </div>
      </div>
      
      {showDropdown && filteredSkills.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredSkills.map((skill, index) => (
            <div
              key={skill}
              onClick={() => handleSelect(skill)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 cursor-pointer text-sm ${
                index === highlightedIndex
                  ? 'bg-slate-100 text-slate-900'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {skill}
            </div>
          ))}
        </div>
      )}
      
      <p className="text-xs text-slate-500">
        Start typing to search from {PREDEFINED_SKILLS.length} predefined skills, or press Enter to add custom skills
      </p>
    </div>
  );
}
